import { useState } from "react";
import { useLocation } from "react-router";
import {
  Menu,
  Search,
  Bell,
  Plus,
  Calendar,
  CalendarDays,
  Tag,
  MapPin,
  Film,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLayout, type QuickAddKind } from "./LayoutContext";
import { Avatar } from "./Avatar";
import { titleForPath } from "./navigation";
import type { LucideIcon } from "lucide-react";

const QUICK_ADD_ITEMS: { kind: Exclude<QuickAddKind, null>; label: string; icon: LucideIcon }[] = [
  { kind: "event", label: "Wydarzenie", icon: Calendar },
  { kind: "parent-event", label: "Cykl wydarzeń", icon: CalendarDays },
  { kind: "category", label: "Kategoria", icon: Tag },
  { kind: "venue", label: "Lokalizacja", icon: MapPin },
  { kind: "movie", label: "Film", icon: Film },
];

export function Topbar() {
  const { pathname } = useLocation();
  const { session, userRole } = useAuth();
  const { setMobileOpen, setCommandOpen, setQuickAdd } = useLayout();
  const [addOpen, setAddOpen] = useState(false);
  const title = titleForPath(pathname);
  const email = session?.user?.email ?? "?";
  const isAdmin = userRole === "admin";

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-black/5 bg-pulso-surface px-4 sm:px-6">
      {/* Mobile hamburger */}
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-pulso-muted hover:bg-black/5 hover:text-foreground lg:hidden"
        aria-label="Menu"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="font-brand text-lg font-bold tracking-tight text-foreground">{title}</div>

      <div className="flex-1" />

      {/* Global search → opens command palette */}
      <button
        type="button"
        onClick={() => setCommandOpen(true)}
        className="hidden h-9 w-72 items-center gap-2 rounded-lg border border-transparent bg-black/5 px-3 text-sm text-pulso-muted transition-colors hover:bg-black/10 sm:flex"
      >
        <Search className="h-4 w-4" />
        <span>Szukaj lub przejdź do...</span>
        <span className="flex-1" />
        <kbd className="rounded border border-black/10 bg-white/70 px-1.5 py-0.5 text-[11px] font-semibold">
          ⌘K
        </kbd>
      </button>

      {/* Compact search on mobile */}
      <button
        type="button"
        onClick={() => setCommandOpen(true)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-pulso-muted hover:bg-black/5 hover:text-foreground sm:hidden"
        aria-label="Szukaj"
      >
        <Search className="h-5 w-5" />
      </button>

      {/* Notifications (placeholder) */}
      <button
        type="button"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-pulso-muted hover:bg-black/5 hover:text-foreground"
        aria-label="Powiadomienia"
      >
        <Bell className="h-[18px] w-[18px]" />
        <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-pulso-orange ring-2 ring-pulso-surface" />
      </button>

      {/* Quick-add menu (admins only — organizers don't have the admin create flow) */}
      {isAdmin && (
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button className="gap-1.5">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Dodaj</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-80" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-1.5">
            <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-pulso-muted">
              Dodaj nowy
            </div>
            {QUICK_ADD_ITEMS.map(({ kind, label, icon: Icon }) => (
              <button
                key={kind}
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  setQuickAdd(kind);
                }}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-black/5"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-pulso-primary/10 text-pulso-primary">
                  <Icon className="h-4 w-4" />
                </span>
                {label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      <Avatar email={email} size={32} />
    </header>
  );
}
